import axios, { AxiosInstance, AxiosResponse } from 'axios';

// Типы для API
export interface Partner {
  _id: string;
  userId: string;
  name: string;
  balance: string;
  telegram?: string;
  status: 'active' | 'inactive' | 'suspended';
  tier: 'bronze' | 'silver' | 'gold';
  campaignIds: string[];
  metadata: Record<string, any>;
  createdAt: string;
  updatedAt: string;
}


// Новые интерфейсы для статистики доходов
export interface PartnerCommission {
  _id: string;
  partnerId: string;
  campaignId: string;
  paymentId: string;
  userId: string;
  paymentAmount: string;
  commissionAmount: string;
  status: 'pending' | 'paid' | 'cancelled';
  paidAt?: string;
  updatedAt: string;
  metadata: Record<string, any>;
  createdAt: string;
}

export interface CreatePartnerCampaignDto {
  name: string;
  description?: string;
}

export interface PartnerApplicationDto {
  telegram: string;
  trafficSources: string;
  views: string;
  subscribers: string;
  description: string;
}

export interface PartnerCampaign {
  _id: string;
  name: string;
  description: string;
  utmSource: string;
  utmMedium: string;
  utmCampaign: string;
  utmContent?: string;
  utmTerm?: string;
  status: 'active' | 'inactive' | 'archived';
  createdBy: string;
  partnerName?: string;
  notes?: string;
  createdAt: string;
  updatedAt: string;
  campaignUrl: string;
}

export interface CampaignStats {
  registrations: number;
  deposits: number;
  depositsAmount: number;
  conversion: number;
  conversionRate: string;
}

export interface PartnerCampaignWithStats extends PartnerCampaign {
  stats: CampaignStats;
}

export interface PartnerCampaignsStatsResponse {
  campaigns: PartnerCampaignWithStats[];
  total: number;
}

export interface PartnerCampaignStats {
  visits: number;
  registrations: number;
  firstDeposits: number;
  depositsCreated: number;
  repeatedDeposits: number;
  totalDeposits: number;
  totalDepositAmount: string;
  registrationToFirstDepositRate: number;
  depositSuccessRate: number;
  avgDepositAmount: string;
}

export interface PartnerProfileResponse {
  partner: Partner;
}

export interface PartnerCommissionsResponse {
  commissions: PartnerCommission[];
  total: number;
}

export interface PartnerCampaignsResponse {
  campaigns: PartnerCampaign[];
  total: number;
}

// Типы для промокодов
export interface PromoCode {
  _id: string;
  code: string;
  bonusPercent: number;
  type: 'DEPOSIT';
  partnerId?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreatePromoCodeDto {
  code: string;
  bonusPercent: number;
  type: 'DEPOSIT';
}

export interface PartnerPromoCodeStats {
  totalPromoCodes: number;
  weeklyCreated: number;
  weeklyLimit: number;
  canCreateMore: boolean;
}

export interface PartnerPromoStats {
  totalPromoCodes: number;
  totalActivations: number;
  totalDeposits: number;
  totalDepositAmount: number;
}

export interface PartnerDashboard {
  balance: number;
  totalDepositAmount: number;
  currentTier: string;
  nextTier?: string;
  progressToNext?: number;
  remainingToNext?: number;
  isMaxTier: boolean;
}

export interface PromoCodeActivation {
  _id: string;
  code: string;
  bonus: number;
  status: 'apply' | 'expired' | 'bonusPaid';
  user: {
    _id?: string;
    steamId: string;
    avatar: string;
    nickname: string;
  };
  createdAt: string;
  updatedAt: string;
}

export interface PartnerPromoCodesResponse {
  data: PromoCode[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface PromoCodeActivationsResponse {
  data: PromoCodeActivation[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

// Типы для уровней партнеров
export interface PartnerTierProgress {
  currentTier: 'bronze' | 'silver' | 'gold';
  totalEarnings: number;
  currentTierThreshold: number;
  nextTier?: 'bronze' | 'silver' | 'gold';
  nextTierThreshold?: number;
  progressToNext?: number;
  remainingToNext?: number;
  isMaxTier: boolean;
}

export interface TierUpgradeCheckResult {
  upgraded: boolean;
  currentTier: 'bronze' | 'silver' | 'gold';
  totalEarnings: number;
  message: string;
}

// Интерфейсы для партнерских выплат
export interface PartnerWithdrawal {
  _id: string;
  userId: string;
  usdtWallet: string;
  amount: string;
  usdtAmount: string;
  status: 'pending' | 'completed' | 'rejected';
  adminComment?: string;
  rejectionReason?: string;
  transactionUrl?: string;
  processedAt?: string;
  completedAt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreatePartnerWithdrawalDto {
  usdtWallet: string;
  amount: number;
}

export interface PartnerWithdrawalListResponse {
  items: PartnerWithdrawal[];
  total: number;
  page: number;
  limit: number;
}

export interface PartnerWithdrawalStats {
  total: number;
  totalWithdrawals: number;
  totalAmount: string;
  pendingWithdrawals: number;
  completedWithdrawals: number;
  rejectedWithdrawals: number;
}

// API клиент
class PartnerApiClient {
  private client: AxiosInstance;
  private profileRequest: Promise<Partner> | null = null;

  constructor() {
    this.client = axios.create({
      baseURL: process.env.REACT_APP_API_URL || 'http://localhost:3021',
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Добавляем токен к каждому запросу
    this.client.interceptors.request.use((config) => {
      const token = localStorage.getItem('partner_token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });

    this.client.interceptors.response.use(
      (response) => response,
      (error) => {
        const status = error?.response?.status;
        if (status === 401) {
          localStorage.removeItem('partner_token');
          try {
            if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/login')) {
              window.location.href = '/login';
            }
          } catch (_) {}
        }
        return Promise.reject(error);
      }
    );
  }

  // Получить профиль партнера
  async getPartnerProfile(): Promise<Partner> {
    if (!this.profileRequest) {
      this.profileRequest = this.client
        .get('/partner/profile')
        .then((response: AxiosResponse<Partner>) => response.data)
        .finally(() => {
          this.profileRequest = null;
        });
    }
    return this.profileRequest;
  }

  // Получить данные дашборда партнера
  async getPartnerDashboard(): Promise<PartnerDashboard> {
    const response: AxiosResponse<PartnerDashboard> = await this.client.get('/partner/dashboard');
    return response.data;
  }

  // Получить комиссии партнера
  async getPartnerCommissions(page: number = 1, limit: number = 20): Promise<PartnerCommissionsResponse> {
    const response: AxiosResponse<PartnerCommissionsResponse> = await this.client.get('/partner/commissions', {
      params: { page, limit }
    });
    return response.data;
  }

  // Промокоды партнера
  async getPartnerPromoCodes(page: number = 1, limit: number = 20, search?: string): Promise<PartnerPromoCodesResponse> {
    const response: AxiosResponse<PartnerPromoCodesResponse> = await this.client.get('/partner/promocodes', {
      params: { page, limit, search }
    });
    return response.data;
  }

  async createPartnerPromoCode(dto: CreatePromoCodeDto): Promise<PromoCode> {
    const response: AxiosResponse<PromoCode> = await this.client.post('/partner/promocodes', dto);
    return response.data;
  }

  async getPartnerPromoCodeStats(): Promise<PartnerPromoCodeStats> {
    const response: AxiosResponse<PartnerPromoCodeStats> = await this.client.get('/partner/promocodes/stats');
    return response.data;
  }



  async getPartnerPromoCodeActivations(
    promoCodeId: string, 
    page: number = 1, 
    limit: number = 20, 
    search?: string,
    status?: string
  ): Promise<PromoCodeActivationsResponse> {
    const response: AxiosResponse<PromoCodeActivationsResponse> = await this.client.get(`/partner/promocodes/${promoCodeId}/activations`, {
      params: { page, limit, search, status }
    });
    return response.data;
  }

  async checkTierUpgrade(): Promise<TierUpgradeCheckResult> {
    const response: AxiosResponse<TierUpgradeCheckResult> = await this.client.post('/partner/check-tier-upgrade');
    return response.data;
  }

  // Методы для работы с партнерскими выплатами
  async createPartnerWithdrawal(dto: CreatePartnerWithdrawalDto): Promise<PartnerWithdrawal> {
    const response: AxiosResponse<PartnerWithdrawal> = await this.client.post('/partner-withdrawal', dto);
    return response.data;
  }

  async getPartnerWithdrawals(page: number = 1, limit: number = 20, status?: string): Promise<PartnerWithdrawalListResponse> {
    const response: AxiosResponse<PartnerWithdrawalListResponse> = await this.client.get('/partner-withdrawal/my', {
      params: { page, limit, status }
    });
    return response.data;
  }

  async getPartnerWithdrawalStats(): Promise<PartnerWithdrawalStats> {
    const response: AxiosResponse<PartnerWithdrawalStats> = await this.client.get('/partner-withdrawal/stats');
    return response.data;
  }

  async cancelPartnerWithdrawal(withdrawalId: string): Promise<PartnerWithdrawal> {
    const response: AxiosResponse<PartnerWithdrawal> = await this.client.put(`/partner-withdrawal/${withdrawalId}/cancel`);
    return response.data;
  }

  // Отправить заявку на партнерство
  async submitPartnerApplication(dto: PartnerApplicationDto): Promise<{ success: boolean; message: string }> {
    const response: AxiosResponse<{ success: boolean; message: string }> = await this.client.post('/partner/applications', dto);
    return response.data;
  }

  // Проверить статус авторизации
  async checkAuth(): Promise<boolean> {
    try {
      await this.getPartnerProfile();
      return true;
    } catch (error) {
      return false;
    }
  }
}

export const partnerApi = new PartnerApiClient();